import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MDButton } from '../../components/MDButton';
import { MDTypography } from '../../components/MDTypography';
import { MDInput } from '../../components/MDInput';
import { Bill } from './ClaimDetailsTypes';
import { BillVerdictStatus } from 'types';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 450,
      maxWidth: 600,
      padding: theme.spacing(2),
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minHeight: 200,
  },
  billInfo: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  billInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  billInfoValue: {
    fontWeight: 500,
  },
  amountInput: {
    marginTop: theme.spacing(1),
  },
  textArea: {
    '& .MuiInputBase-root': {
      minHeight: 80,
    },
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
}));

interface ApprovalModalProps {
  open: boolean;
  bill: Bill | null;
  claimId: string;
  loading: boolean;
  onClose: () => void;
  onApprove: (
    billId: number,
    amount: number,
    reason: string,
    verdict: BillVerdictStatus
  ) => void;
}

export const ApprovalModal: React.FC<ApprovalModalProps> = ({
  open,
  bill,
  claimId,
  loading,
  onClose,
  onApprove,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [approveFullBill, setApproveFullBill] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [reason, setReason] = useState('');
  const [amountError, setAmountError] = useState('');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  useEffect(() => {
    if (open && bill) {
      // Reset form when modal opens
      setApproveFullBill(true);
      setCustomAmount('');
      setReason('Approved full billed amount');
      setAmountError('');
    }
  }, [open, bill]);

  useEffect(() => {
    // Update reason text based on checkbox state
    if (approveFullBill) {
      setReason('Approved full billed amount');
    } else {
      setReason('');
    }
  }, [approveFullBill]);

  const validateAmount = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setAmountError('Amount is required');
      return false;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setAmountError('Please enter a valid number');
      return false;
    }

    if (numValue < 1) {
      setAmountError('Amount must be at least $1.00');
      return false;
    }

    if (bill && numValue > bill.billedCharges) {
      setAmountError(
        `Amount cannot exceed bill amount of ${formatCurrency(
          bill.billedCharges
        )}`
      );
      return false;
    }

    setAmountError('');
    return true;
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return;
    }

    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      return;
    }

    setCustomAmount(numericValue);
    if (numericValue) {
      validateAmount(numericValue);
    } else {
      setAmountError('');
    }
  };

  const handleApprove = () => {
    if (!bill) return;

    let amount = bill.billedCharges;
    let verdict: BillVerdictStatus = 'approved';

    if (!approveFullBill) {
      if (!validateAmount(customAmount)) {
        return;
      }
      amount = parseFloat(customAmount);
      verdict = 'partial-approved';
    }

    if (!reason.trim()) {
      return;
    }

    onApprove(bill.id, amount, reason.trim(), verdict);
  };

  const isFormValid = () => {
    if (!reason.trim()) return false;
    if (!approveFullBill) {
      return customAmount && !amountError;
    }
    return true;
  };

  if (!bill) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.title}>
        <MDTypography variant="h6" fontWeight="medium">
          Approve Bill - {bill.gemBillNo}
        </MDTypography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.billInfo}>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Bill ID:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {bill.gemBillNo}
            </MDTypography>
          </div>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Provider:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {bill.payableTo}
            </MDTypography>
          </div>
          <div className={classes.billInfoRow}>
            <MDTypography variant="body2" color="text">
              Bill Amount:
            </MDTypography>
            <MDTypography variant="body2" fontWeight="medium">
              {formatCurrency(bill.billedCharges)}
            </MDTypography>
          </div>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={approveFullBill}
              onChange={(e) => setApproveFullBill(e.target.checked)}
              color="primary"
            />
          }
          label="Approve Full Bill"
        />

        {!approveFullBill && (
          <Box className={classes.amountInput}>
            <MDInput
              label="Custom Approval Amount (USD)"
              value={customAmount}
              onChange={handleAmountChange}
              placeholder="0.00"
              fullWidth
              error={!!amountError}
            />
            {amountError ? (
              <MDTypography
                variant="caption"
                color="error"
                style={{ marginTop: '4px', display: 'block' }}
              >
                {amountError}
              </MDTypography>
            ) : (
              <MDTypography
                variant="caption"
                color="text"
                style={{ marginTop: '4px', display: 'block' }}
              >
                Maximum: {formatCurrency(bill.billedCharges)}
              </MDTypography>
            )}
          </Box>
        )}

        <MDInput
          label="Reason / Comments"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          rows={3}
          fullWidth
          placeholder="Enter approval reason or comments..."
        />
      </DialogContent>

      <DialogActions className={classes.actions}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="success"
          onClick={handleApprove}
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Processing...' : 'Approve'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalModal;
